import { GoogleMap, Marker, MarkerClusterer, useLoadScript } from '@react-google-maps/api';
import { mapStyle } from 'resources/theme/GoogleMapStyle';
import config from 'config';
import { useIsMobileView } from 'hooks';
import { Center, CenterIncludedFile, CenterIncludedService } from 'models/centers.model';
import { FC, useCallback, useEffect, useState } from 'react';
import MapMarker from './MapMarker';

interface MapCentersProps {
  centersInfo: {
    center: Center;
    file: CenterIncludedFile;
    services: CenterIncludedService[];
  }[];
  myPosition:
    | {
        lat: number;
        lng: number;
      }
    | undefined;
  markersState: boolean[];
  handleOpenMapMarker: (index: number) => void;
  handleCloseMapMarker: () => void;
}

const defaultMapCenter = { lat: 49.8153, lng: 6.1296 }; // Luxembourg
const defaultMapZoom = 9;

const MapCenters: FC<MapCentersProps> = ({ centersInfo, myPosition, markersState, handleOpenMapMarker }) => {
  const { isLoaded: isGoogleLoaded /* , loadError: googleLoadError */ } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey || '',
  });
  const isMobileView = useIsMobileView();
  const [map, setMap] = useState<google.maps.Map>();
  const handleLoad = useCallback(function onLoad(mapInstance: google.maps.Map) {
    setMap(mapInstance);
  }, []);

  useEffect(() => {
    if (map && myPosition && !isMobileView) {
      map.panTo(myPosition);
      google.maps.event.trigger(map, 'resize');
    }
  }, [myPosition, map, isMobileView]);

  return (
    <>
      {isGoogleLoaded && (
        <GoogleMap onLoad={handleLoad} center={defaultMapCenter} zoom={defaultMapZoom} mapContainerStyle={{ height: '100%', width: '100%' }} options={{ styles: mapStyle }}>
          <MarkerClusterer>
            {(clusterer) => (
              <>
                {centersInfo.map(
                  (info, index) =>
                    info.center.attributes.field_prelevement_latitude &&
                    info.center.attributes.field_prelevement_longitude && (
                      <MapMarker
                        key={index}
                        position={{
                          lat: parseFloat(info.center.attributes.field_prelevement_latitude.replaceAll(',', '.')),
                          lng: parseFloat(info.center.attributes.field_prelevement_longitude.replaceAll(',', '.')),
                        }}
                        opened={markersState[index]}
                        handleOpen={handleOpenMapMarker}
                        index={index}
                        clusterer={clusterer}
                      />
                    ),
                )}
              </>
            )}
          </MarkerClusterer>
          {myPosition && (
            <Marker
              position={myPosition}
              icon='data:image/png;base64,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'
              animation={google.maps.Animation.DROP}
            />
          )}
        </GoogleMap>
      )}
    </>
  );
};

export default MapCenters;
